/**
 * stats.js — Pure statistics engine for the bet-tracking hub.
 *
 * Every function here is pure (same input -> same output, no I/O, no
 * mutation of inputs, no framework imports) so the exact same code runs
 * in Node tests today, the React website tomorrow, and React Native
 * later.
 *
 * Definitions used throughout (kept in sync with README.md):
 * - SETTLED bets: status won / lost / push / cashed_out.
 * - DECIDED bets: status won / lost only.
 * - TOTAL STAKED: sum of stakes over SETTLED bets (pending stakes are
 *   tracked separately as pending exposure). ROI is defined against
 *   settled stakes so an open bet never distorts it.
 * - NET PROFIT: sum of profitForBet() over settled bets. A cashed_out
 *   bet contributes (cashOutAmount - stake). Bets whose profit cannot
 *   be valued (e.g. a cashed_out bet missing cashOutAmount) contribute
 *   their stake to totalStaked but 0 to profit, and are counted in
 *   `unvaluedSettled` so the UI can warn if it ever happens.
 * - ROI: netProfit / totalStaked. null when totalStaked is 0.
 * - WIN RATE: wins / (wins + losses). Pushes and cash-outs are excluded
 *   from both the numerator and denominator. null when no decided bets.
 * - RECORD: { wins, losses, pushes } (cash-outs reported separately).
 * - BIGGEST WIN: the won bet with the highest profit. BIGGEST LOSS: the
 *   lost bet with the most negative profit. Ties break to the earliest
 *   settled bet. Cash-outs are eligible for neither (they are neither
 *   won nor lost).
 * - CLOSEST WIN: among won bets with a known decision margin, the one
 *   with the SMALLEST margin (won by the least). CLOSEST LOSS: among
 *   lost bets with a known margin, the one whose margin is CLOSEST TO
 *   ZERO (lost by the least). See model.js for the margin definition
 *   and the parlay leg rule. Ties break to the earliest settled bet.
 * - STREAKS: computed over decided bets only, in settlement order
 *   (settledAt, falling back to placedAt). Pushes and cash-outs are
 *   SKIPPED — they neither extend nor break a streak.
 */

import {
  round2,
  profitForBet,
  decisionMargin,
  isSettled,
  isDecided,
} from './model.js';

function settledTime(bet) {
  const t = Date.parse(bet.settledAt ?? bet.placedAt);
  return Number.isNaN(t) ? 0 : t;
}

function bySettlement(a, b) {
  return settledTime(a) - settledTime(b);
}

/** Sum of stakes of pending (open) bets — money currently at risk. */
export function pendingExposure(bets) {
  let total = 0;
  for (const bet of bets) {
    if (bet.status === 'pending') total += bet.stake;
  }
  return round2(total);
}

/**
 * Longest / current streaks over decided bets (see header for the
 * skip-pushes-and-cash-outs rule).
 * Returns { current: {type:'W'|'L', length} | null,
 *           longestWin: number, longestLoss: number }.
 */
export function computeStreaks(bets) {
  const decided = bets.filter(isDecided).slice().sort(bySettlement);
  let longestWin = 0;
  let longestLoss = 0;
  let runType = null;
  let runLength = 0;
  for (const bet of decided) {
    const type = bet.status === 'won' ? 'W' : 'L';
    if (type === runType) {
      runLength += 1;
    } else {
      runType = type;
      runLength = 1;
    }
    if (type === 'W') longestWin = Math.max(longestWin, runLength);
    else longestLoss = Math.max(longestLoss, runLength);
  }
  return {
    current: runType === null ? null : { type: runType, length: runLength },
    longestWin,
    longestLoss,
  };
}

/**
 * Cumulative profit time series for charting: one point per settled bet
 * (in settlement order), starting from { at: null, cumulative: 0 } so a
 * chart can anchor at the origin.
 * Returns [{ at: ISO string|null, betId, profit, cumulative }].
 */
export function cumulativeProfitSeries(bets) {
  const settled = bets.filter(isSettled).slice().sort(bySettlement);
  const points = [{ at: null, betId: null, profit: 0, cumulative: 0 }];
  let cumulative = 0;
  for (const bet of settled) {
    const profit = profitForBet(bet) ?? 0;
    cumulative = round2(cumulative + profit);
    points.push({
      at: bet.settledAt ?? bet.placedAt,
      betId: bet.id,
      profit,
      cumulative,
    });
  }
  return points;
}

/**
 * Core aggregate over a list of bets. Used directly for the hub header
 * and per-group in breakdowns (groups omit the highlight/streak fields).
 */
export function summarizeBets(bets) {
  let wins = 0;
  let losses = 0;
  let pushes = 0;
  let cashOuts = 0;
  let pendingCount = 0;
  let totalStaked = 0;
  let netProfit = 0;
  let unvaluedSettled = 0;

  let biggestWin = null; // { bet, profit }
  let biggestLoss = null;
  let closestWin = null; // { bet, margin }
  let closestLoss = null;

  const settledSorted = bets.filter(isSettled).slice().sort(bySettlement);

  for (const bet of bets) {
    if (bet.status === 'pending') {
      pendingCount += 1;
      continue;
    }
    if (!isSettled(bet)) continue;
    totalStaked += bet.stake;
    const profit = profitForBet(bet);
    if (profit === null) unvaluedSettled += 1;
    else netProfit += profit;

    if (bet.status === 'won') wins += 1;
    else if (bet.status === 'lost') losses += 1;
    else if (bet.status === 'push') pushes += 1;
    else if (bet.status === 'cashed_out') cashOuts += 1;
  }

  // Highlights in settlement order, so every tie keeps the earliest bet.
  for (const bet of settledSorted) {
    const profit = profitForBet(bet);
    if (bet.status === 'won' && profit !== null &&
        (biggestWin === null || profit > biggestWin.profit)) {
      biggestWin = { bet, profit };
    }
    if (bet.status === 'lost' && profit !== null &&
        (biggestLoss === null || profit < biggestLoss.profit)) {
      biggestLoss = { bet, profit };
    }
    if (bet.status !== 'won' && bet.status !== 'lost') continue;
    const margin = decisionMargin(bet);
    if (margin === null) continue;
    if (bet.status === 'won') {
      if (closestWin === null || margin < closestWin.margin) {
        closestWin = { bet, margin };
      }
    } else if (closestLoss === null || margin > closestLoss.margin) {
      closestLoss = { bet, margin };
    }
  }

  const decidedCount = wins + losses;
  const streaks = computeStreaks(bets);

  return {
    totalBets: bets.length,
    settledBets: wins + losses + pushes + cashOuts,
    pendingBets: pendingCount,
    wins,
    losses,
    pushes,
    cashOuts,
    record: { wins, losses, pushes },
    winRate: decidedCount > 0 ? wins / decidedCount : null,
    totalStaked: round2(totalStaked),
    pendingExposure: pendingExposure(bets),
    netProfit: round2(netProfit),
    roi: totalStaked > 0 ? netProfit / totalStaked : null,
    unvaluedSettled,
    biggestWin,
    biggestLoss,
    closestWin,
    closestLoss,
    currentStreak: streaks.current,
    longestWinStreak: streaks.longestWin,
    longestLossStreak: streaks.longestLoss,
  };
}

/**
 * Group bets and summarize each group. Returns an array of
 * { key, label, totalBets, settledBets, pendingBets, wins, losses,
 *   pushes, cashOuts, totalStaked, netProfit, roi, winRate }
 * sorted by netProfit descending (most profitable group first).
 */
function groupBreakdown(bets, keyOf, labelOf) {
  const groups = new Map();
  for (const bet of bets) {
    const key = keyOf(bet);
    if (!groups.has(key)) groups.set(key, { key, label: labelOf(bet), bets: [] });
    groups.get(key).bets.push(bet);
  }
  const rows = [];
  for (const group of groups.values()) {
    const s = summarizeBets(group.bets);
    rows.push({
      key: group.key,
      label: group.label,
      totalBets: s.totalBets,
      settledBets: s.settledBets,
      pendingBets: s.pendingBets,
      wins: s.wins,
      losses: s.losses,
      pushes: s.pushes,
      cashOuts: s.cashOuts,
      totalStaked: s.totalStaked,
      netProfit: s.netProfit,
      roi: s.roi,
      winRate: s.winRate,
    });
  }
  rows.sort((a, b) => b.netProfit - a.netProfit);
  return rows;
}

export function breakdownBySport(bets) {
  return groupBreakdown(bets, (b) => b.sport, (b) => b.sport);
}

export function breakdownByBook(bets) {
  return groupBreakdown(bets, (b) => b.book.key, (b) => b.book.name);
}

export function breakdownByBetType(bets) {
  return groupBreakdown(bets, (b) => b.betType, (b) => b.betType);
}

/** Month key is the YYYY-MM of placedAt (ISO, i.e. UTC month). */
export function breakdownByMonth(bets) {
  const rows = groupBreakdown(
    bets,
    (b) => (b.placedAt ?? '').slice(0, 7),
    (b) => (b.placedAt ?? '').slice(0, 7),
  );
  // Chronological reads better than profit order for a time breakdown.
  rows.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return rows;
}

/**
 * Convenience filter used by list UIs.
 * filter: { status?, sport?, bookKey?, betType? } — all optional, ANDed.
 * Returns a new array; input order preserved.
 */
export function filterBets(bets, filter = {}) {
  return bets.filter((bet) => {
    if (filter.status && bet.status !== filter.status) return false;
    if (filter.sport && bet.sport !== filter.sport) return false;
    if (filter.bookKey && bet.book.key !== filter.bookKey) return false;
    if (filter.betType && bet.betType !== filter.betType) return false;
    return true;
  });
}
